import * as BaseApi from "api/lib/data/Base";
import * as _ from "lodash";
import * as React from "react";
import moment from "moment";

import { Table, Input, Segment, Header } from "semantic-ui-react";
import { cloneWithOutReference } from "src/utils";
import ListUsers from "../Query/ListUsers";
import { Base, IBaseProps, IBaseState, ResourceTypeLabel } from "./Base";
import DeleteResource from "./DeleteResource";
import { UsersStatus } from "../Session/withAuthorization";

const timeFromNow = (timestamp: object) => moment(timestamp).fromNow();

export interface IColumn {
  name: string;
  header: string;
  type: "string" | "number" | "date";
  sort: boolean;
  search: boolean;
  display: (row: any) => React.ReactNode;
}

export interface IListBaseProps extends IBaseProps {
  authUser: any;
  orgId: string;
  usersStatus: UsersStatus;
}

export interface IListBaseState extends IBaseState {
  columns: IColumn[];
  user: any;
  orgId: string;
  resourceType: ResourceTypeLabel;
  sortColumn: string;
  data: any[];
  filteredData: any[];
  direction?: "ascending" | "descending";
  openShareModal: boolean;
  members: any[];
  membersById: any;
  selectedUser: any;
}

export default abstract class ListBase<
  TProps extends IListBaseProps,
  TState extends IListBaseState
> extends Base<TProps, TState> {
  protected static LIST_BASE_INITAL_STATE = {
    ...Base.BASE_INITAL_STATE,
    columns: [],
    resourceType: "",
    sortColumn: "",
    data: [],
    filteredData: [],
    direction: undefined,
    listeners: [],
    orgId: "",
    user: null,
    openShareModal: false,
    members: [],
    membersById: {},
    selectedUser: null,
  };

  private api: BaseApi.default;

  protected abstract goToResource(id: string): void;
  protected abstract addResourceButton(): React.ReactNode;

  constructor(props: IListBaseProps, api: BaseApi.default) {
    super(props);
    this.api = api;
  }

  protected renderToolbar = () => {
    return (
      <div style={{ padding: "5px 0", height: "50px" }}>
        <div style={{ float: "left" }}>
          <Input
            icon="search"
            iconPosition="left"
            name="search"
            onClick={(e: React.BaseSyntheticEvent) => e.stopPropagation()}
            onChange={this.handleSearchInputChange}
            placeholder={`Search ${this.state.resourceType.plural}`}
            style={{ width: "100%", padding: "0" }}
          />
        </div>
        <div style={{ float: "right" }}>{this.addResourceButton()}</div>
      </div>
    );
  };

  private handleSearchInputChange = (
    e: React.SyntheticEvent,
    { value }: any
  ) => {
    const data = cloneWithOutReference(this.state.data);
    if (value) {
      const filteredData = _.filter(data, (o) =>
        _.toLower(o.name).includes(_.toLower(value))
      );
      this.setState({ filteredData });
    } else {
      this.setState({ filteredData: data });
    }
  };

  private renderListAndInviteModel() {
    const {
      columns,
      resourceType,
      sortColumn,
      filteredData,
      direction,
    } = this.state;

    return (
      <React.Fragment>
        <Table
          sortable={true}
          celled={true}
          fixed={true}
          striped={true}
          compact="very"
        >
          <Table.Header>
            <Table.Row>
              {columns.map((column: IColumn, columnIndex) => {
                return (
                  <Table.HeaderCell
                    key={`colKey_${columnIndex}`}
                    sorted={sortColumn === column.name ? direction : undefined}
                    onClick={this.handleSort(column.name)}
                  >
                    {column.header}
                  </Table.HeaderCell>
                );
              })}
              <Table.HeaderCell>Users</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {_.map(filteredData, (row) => (
              <Table.Row key={row.name}>
                {columns.map((column: IColumn, columnIndex) => {
                  return (
                    <Table.Cell key={`${row.name}_rowColKey_${columnIndex}`}>
                      {column.display(row)}
                    </Table.Cell>
                  );
                })}
                <Table.Cell>
                  <ListUsers
                    {...this.props}
                    resource={{
                      id: row.id,
                      name: row.name,
                      api: this.api,
                      type: resourceType,
                    }}
                  />
                </Table.Cell>
                <Table.Cell>
                  {row.roles && (row.roles.admin || row.roles.owner) && (
                    <DeleteResource
                      {...this.props}
                      resource={{
                        id: row.id,
                        name: row.name,
                        api: this.api,
                        type: resourceType,
                      }}
                      deleted={() => this.getData()}
                    />
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </React.Fragment>
    );
  }

  public render() {
    return (
      <Segment className="dashboards_segment_main">
        {this.renderToolbar()}
        {this.renderListAndInviteModel()}
      </Segment>
    );
  }

  protected getData = () => {
    const { authUser } = this.props;
    this.getList(this.api, authUser.uid, (data: any[]) => {
      this.setState({ data, filteredData: cloneWithOutReference(data) });
    });
    this.getMembers((membersData) => {
      const membersById = {};
      membersData.forEach((m) => {
        membersById[m.id] = m;
      });
      this.setState({ members: membersData, membersById });
    });
  };

  private handleSort = (clickedColumn: string) => () => {
    const { sortColumn, filteredData, direction } = this.state;

    if (sortColumn !== clickedColumn) {
      this.setState({
        sortColumn: clickedColumn,
        filteredData: _.sortBy(filteredData, [clickedColumn]),
        direction: "ascending",
      });

      return;
    }

    this.setState({
      filteredData: filteredData.reverse(),
      direction: direction === "ascending" ? "descending" : "ascending",
    });
  };

  protected displayNameColumn = (row: any) => {
    const { membersById } = this.state;
    return (
      <a>
        <Header as="h4" onClick={() => this.goToResource(row.id)}>
          <Header.Content className="list-base-first-row-header">
            {row.name}
            {row.audit && (
              <Header.Subheader className="list_table_name_sub_header">
                Created by{" "}
                {membersById &&
                  membersById[row.audit.createdBy] &&
                  membersById[row.audit.createdBy].name}{" "}
                {timeFromNow(row.audit.createdAt)}
              </Header.Subheader>
            )}
          </Header.Content>
        </Header>
      </a>
    );
  };
}



// WEBPACK FOOTER //
// ./src/components/Common/ListBase.tsx